<template>
	<div>
		<BasePlaceHolder
			v-if="isAuthenticated"
			:placehold="loading"
			:line-count="2"
			:theme="isDarkMode ? 'dark' : 'light'"
			:class="{ 'display-flex justify-content-center': centerButton && !loading }"
		>
			<BaseLink
				v-if="link"
				:class="['button button-primary', { 'width-100': !appStore.isDesktop }]"
				:link="link"
				:data-test="`${componentId}-auth-link`"
			/>
			<BaseButton
				v-else
				:class="['button button-primary', { 'width-100': !appStore.isDesktop }]"
				:data-test="`${componentId}-ocf-link`"
				@click="openOffRampModal()"
			>
				Invest
			</BaseButton>
		</BasePlaceHolder>
		<LeadCaptureComponent
			v-else-if="props.formConfig"
			:form-config="props.formConfig"
			:component-id="props.componentId"
		/>
	</div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue';
import CheckoutPresetInvestmentExitModal from '@components/checkout/checkout-preset-investment-exit-modal.vue';
import { FundDirectInvestDetails } from 'types/funds';
import { getFund } from '@sanity/proxy/funds';
import { getInvestFlowLink } from '@utils/invest';
import { getResumeCheckoutModule } from '@api/modular-dashboard';
import LeadCaptureComponent from '@components/lead-capture/lead-capture-form.vue';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import { Link } from 'types/layout';
import { ModularDashboardResumeCheckoutModule } from 'types/modular-dashboard/custom';
import { useAppStore } from '@stores/app';
import { useRoute } from 'vue-router';
import { useUserStore } from '@stores/user';

interface Props {
	formConfig?: LeadCaptureFormConfig;
	centerButton?: boolean;
	componentId?: string;
	isDarkMode?: boolean;
	offeringId?: string;
}

const props = withDefaults(defineProps<Props>(), {
	formConfig: undefined,
	centerButton: false,
	componentId: undefined,
	isDarkMode: false,
	offeringId: undefined
});

const route = useRoute();
const appStore = useAppStore();
const userStore = useUserStore();

const isAuthenticated = appStore.isAuthenticated;

const loading = ref(true);
const directInvestDetails = ref<FundDirectInvestDetails | null>(null);
const resumeCheckoutModuleDetails = ref<ModularDashboardResumeCheckoutModule | null>(null);

const link = computed((): Link | undefined => {
	const investLink = { text: 'Invest' };

	if (userStore.isExistingInvestor) {
		if (directInvestDetails.value?.entityEligibleForEDirectInvestmentIntoReit) {
			return {
				...getInvestFlowLink('fund', investLink.text, {
					backRoute: route.name as string,
					offeringId: directInvestDetails.value.reitOfferingVintageId
				}),
				state: { backRoute: route.name }
			};
		} else {
			return getInvestFlowLink('plan', 'Invest', { backRoute: route.name as string });
		}
	} else {
		return getResumeCheckoutLink();
	}
});

function getResumeCheckoutLink(): Link | undefined {
	const defaultLink: Link = {
		text: 'Invest',
		router: 'checkout-resume'
	};

	if (!resumeCheckoutModuleDetails.value) {
		return defaultLink;
	}

	switch (resumeCheckoutModuleDetails.value.module) {
		case 'STANDARD':
			return {
				text: 'Continue investing',
				router: 'checkout-resume'
			};

		case 'IRA_NOT_FUNDED':
		case 'IRA_ROLLOVER':
			return {
				text: 'Continue investing',
				router: 'ira-add-funds-start'
			};

		case 'IRA_INCOMPLETE':
			return {
				text: 'Continue investing',
				router: 'ira-signup-start'
			};
		case 'PRESET_OFFERING_OPEN':
		case 'PRESET_OFFERING_CLOSED':
			return undefined;
		default:
			return defaultLink;
	}
}

function openOffRampModal(): void {
	appStore.updateCurrentModal({
		modal: CheckoutPresetInvestmentExitModal,
		props: { offeringName: 'Opportunistic Credit Fund' }
	});
}

async function setup(): Promise<void> {
	if (isAuthenticated) {
		if (userStore.isExistingInvestor && props.offeringId) {
			const fund = await getFund(props.offeringId);
			directInvestDetails.value = fund?.directInvestmentDetails ?? null;
		} else if (!userStore.isExistingInvestor) {
			try {
				resumeCheckoutModuleDetails.value = await getResumeCheckoutModule();
			} catch {
				resumeCheckoutModuleDetails.value = null;
			}
		}
	}

	loading.value = false;
}

setup();
</script>
